import type {
  HullCleaningCase,
  HullCleaningDashboardFilters,
  HullCleaningDryDock,
  HullCleaningEvent,
  HullCleaningVoyage,
} from "../models/hull-cleaning-dashboard.models";

const equalsFilter = (value: string | null | undefined, filter: string) =>
  !filter || String(value ?? "").toLowerCase() === filter.toLowerCase();

const matchesMultiFilter = (value: string | null | undefined, filters: string[]) =>
  filters.length === 0 || filters.some((filter) => equalsFilter(value, filter));

const withinDateRange = (value: string | null, from: string, to: string) => {
  if (!value) {
    return !from && !to;
  }

  const date = value.slice(0, 10);
  return (!from || date >= from) && (!to || date <= to);
};

export const filterHullCleaningVoyages = (
  rows: HullCleaningVoyage[],
  filters: HullCleaningDashboardFilters,
) => rows.filter((row) => {
  const minimum = filters.portDaysMin === "" ? null : Number(filters.portDaysMin);
  const maximum = filters.portDaysMax === "" ? null : Number(filters.portDaysMax);
  const portDaysMatches =
    minimum === null && maximum === null
      ? true
      : row.portDays !== null &&
        (minimum === null || row.portDays >= minimum) &&
        (maximum === null || row.portDays <= maximum);

  return (
    equalsFilter(row.imo, filters.imo) &&
    equalsFilter(row.vesselName, filters.vesselName) &&
    matchesMultiFilter(row.contractType, filters.contractTypes) &&
    matchesMultiFilter(row.voyageStatus, filters.voyageStatuses) &&
    equalsFilter(row.vesselType, filters.vesselType) &&
    matchesMultiFilter(row.arrDepStatus, filters.arrDepStatuses) &&
    equalsFilter(row.opsCoordinator, filters.opsCoordinator) &&
    matchesMultiFilter(row.oprType, filters.oprTypes) &&
    portDaysMatches
  );
});

export const filterHullCleaningCases = (rows: HullCleaningCase[], filters: HullCleaningDashboardFilters) =>
  rows.filter((row) =>
    equalsFilter(row.imo, filters.imo) &&
    equalsFilter(row.vesselName, filters.vesselName) &&
    equalsFilter(row.requestId, filters.requestId) &&
    equalsFilter(row.status, filters.caseStatus) &&
    equalsFilter(row.bookingDate, filters.bookingDate) &&
    equalsFilter(row.lastUpdated?.slice(0, 10), filters.lastUpdated) &&
    equalsFilter(row.outcomeDate, filters.outcomeDate) &&
    withinDateRange(row.requestDate, filters.requestDateFrom, filters.requestDateTo),
  ).sort((left, right) => {
    const leftDate = left.requestDate ?? left.lastUpdated ?? "";
    const rightDate = right.requestDate ?? right.lastUpdated ?? "";
    const dateOrder = rightDate.localeCompare(leftDate);

    if (dateOrder !== 0) {
      return dateOrder;
    }

    return Number(right.requestId) - Number(left.requestId);
  });

export const hasVoyageRelationshipFilter = (filters: HullCleaningDashboardFilters): boolean =>
  Boolean(
    filters.contractTypes.length > 0 ||
      filters.voyageStatuses.length > 0 ||
      filters.portDaysMin ||
      filters.portDaysMax ||
      filters.vesselType ||
      filters.arrDepStatuses.length > 0 ||
      filters.opsCoordinator ||
      filters.oprTypes.length > 0,
  );

export const keepRowsForFilteredVoyageImos = <T extends { imo: string | null }>(
  rows: T[],
  voyageImos: ReadonlySet<string>,
  shouldRestrict: boolean,
): T[] => shouldRestrict ? rows.filter((row) => row.imo !== null && voyageImos.has(row.imo)) : rows;

export const filterHullCleaningDryDocks = (
  rows: HullCleaningDryDock[],
  filters: HullCleaningDashboardFilters,
) => rows.filter((row) => equalsFilter(row.imo, filters.imo) && equalsFilter(row.vesselName, filters.vesselName));

export const filterHullCleaningEvents = (
  rows: HullCleaningEvent[],
  filters: HullCleaningDashboardFilters,
) => rows.filter((row) => equalsFilter(row.imo, filters.imo) && equalsFilter(row.vesselName, filters.vesselName));

export const uniqueOptions = <T>(rows: T[], readValue: (row: T) => string | null | undefined): string[] =>
  Array.from(new Set(rows.map(readValue).map((value) => String(value ?? "").trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );

export const formatDashboardDate = (value: string | null | undefined): string => {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  }).format(parsed).replace(/ /g, "-");
};

export const formatDashboardTimestamp = (value: string | null | undefined): string => {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(parsed);
};
